/**
 * O que foi lido no codigo de barras / QR.
 *
 * Funcao pura e sem DOM de proposito: a decisao "isto e um objeto dos Correios"
 * e regra de negocio da portaria, nao detalhe do leitor. Separada assim ela
 * pode ser testada com strings e reaproveitada quando o codigo chegar por
 * outro caminho (coletor bluetooth, importacao de lista do entregador).
 *
 * Regra ao adicionar transportadora: so afirme o que o formato realmente
 * garante. Chutar "Jadlog" para qualquer numero de 14 digitos faz a portaria
 * registrar transportadora errada em massa — pior do que deixar o campo vazio
 * para o porteiro escolher.
 */

export type ScanKind =
  /** QR de retirada gerado por este sistema. */
  | 'pickup'
  /** Codigo de objeto de uma transportadora. */
  | 'tracking'
  /** Chave de acesso de NF-e (44 digitos) impressa na DANFE colada no pacote. */
  | 'invoice'
  /** Leu, mas nao reconheceu o formato. Ainda serve como codigo. */
  | 'unknown';

export interface DecodedCode {
  /** Exatamente o que o leitor entregou. */
  raw: string;
  /** Normalizado para gravar: maiusculas, sem espacos nem hifens. */
  value: string;
  kind: ScanKind;
  /** Nome da transportadora quando o formato permite afirmar. */
  carrier: string | null;
  /** Token do QR de retirada — so quando `kind === 'pickup'`. */
  pickupToken: string | null;
}

/** Tira espacos, hifens e pontos: cada transportadora imprime de um jeito. */
export function normalizeCode(value: string): string {
  return value.replace(/[\s.-]+/g, '').toUpperCase();
}

interface CarrierRule {
  carrier: string;
  test: RegExp;
}

const CARRIER_RULES: CarrierRule[] = [
  // Padrao SRO universal (UPU S10): 2 letras + 9 digitos + pais. No Brasil
  // chega como ...BR tanto de objeto nacional quanto de importado.
  { carrier: 'Correios', test: /^[A-Z]{2}\d{9}BR$/ },
  // Amazon Logistics
  { carrier: 'Amazon', test: /^TBA\d{10,}$/ },
  // Shopee Express — sempre prefixado, o que evita confundir com o SRO acima
  // (que tem o BR no FIM, nao no comeco).
  { carrier: 'Shopee', test: /^(SPX)?BR\d{10,}$/ },
  // Mercado Envios Flex
  { carrier: 'Mercado Livre', test: /^ML[A-Z]?\d{8,}$/ },
  { carrier: 'DHL', test: /^JD\d{16,}$/ },
];

/** Dominios que aparecem em QR de etiqueta e identificam a transportadora. */
const CARRIER_DOMAINS: Array<[RegExp, string]> = [
  [/mercadoli(vre|bre)\.com|mercadolibre|melenvios/i, 'Mercado Livre'],
  [/correios\.com\.br|linketrack/i, 'Correios'],
  [/shopee\.com|spx\.com/i, 'Shopee'],
  [/loggi\.com/i, 'Loggi'],
  [/amazon\.com|amzn\./i, 'Amazon'],
  [/jadlog\.com/i, 'Jadlog'],
  [/totalexpress\.com/i, 'Total Express'],
];

/**
 * Token do QR de retirada: 32 bytes em base64url (43 caracteres). Aceitamos de
 * 20 para cima para nao quebrar caso o backend mude o tamanho do segredo.
 */
const PICKUP_TOKEN = /^[A-Za-z0-9_-]{20,200}$/;

/**
 * O mesmo token, mas colado como texto solto — sem a URL em volta.
 *
 * Aqui o teste precisa ser mais rigoroso: "base64url com 20+ caracteres"
 * tambem descreve uma chave de NF-e de 44 digitos e qualquer codigo de
 * rastreio comprido. Confundir os dois faz o leitor de etiqueta anunciar "isso
 * e um QR de retirada" para um pacote que acabou de chegar.
 *
 * A separacao vem do alfabeto: rastreio e chave fiscal sao MAIUSCULAS e
 * digitos, enquanto o token e base64url de bytes aleatorios — em 43
 * caracteres, a chance de nao aparecer nenhuma minuscula ou nenhuma maiuscula
 * e da ordem de 1 em bilhoes.
 */
function looksLikeLooseToken(value: string): boolean {
  if (!/^[A-Za-z0-9_-]{32,200}$/.test(value)) return false;
  return /[a-z]/.test(value) && /[A-Z]/.test(value);
}

function asUrl(value: string): URL | null {
  if (!/^https?:\/\//i.test(value.trim())) return null;
  try {
    return new URL(value.trim());
  } catch {
    return null;
  }
}

/**
 * O QR entregue ao morador contem a URL de retirada inteira
 * (`PICKUP_BASE_URL/<token>`), entao o leitor precisa tirar o token do fim do
 * caminho. Aceita tambem o token puro, que e o que o porteiro cola quando o
 * morador manda o link por mensagem.
 */
export function extractPickupToken(value: string): string | null {
  const trimmed = value.trim();

  const url = asUrl(trimmed);
  if (url) {
    const last = url.pathname.split('/').filter(Boolean).pop();
    return last && PICKUP_TOKEN.test(last) ? last : null;
  }

  return looksLikeLooseToken(trimmed) ? trimmed : null;
}

/**
 * Etiquetas do Mercado Envios trazem um QR com JSON (`{"id":...}`) em vez de um
 * codigo solto. Sem tratar esse caso, o campo de rastreio receberia a chave
 * inteira em JSON.
 */
function decodeJsonLabel(value: string): { code: string; carrier: string } | null {
  if (!value.trim().startsWith('{')) return null;

  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null) return null;

    const data = parsed as Record<string, unknown>;
    const code = data.id ?? data.tracking_number ?? data.sender_id;
    if (code === undefined || code === null) return null;

    return { code: String(code), carrier: 'Mercado Livre' };
  } catch {
    return null;
  }
}

export function decodeScan(raw: string): DecodedCode {
  const trimmed = raw.trim();

  // 1. O nosso proprio QR vem primeiro: e o unico que tem significado de acao
  //    (retirar encomenda) e nao de identificacao.
  const pickupToken = extractPickupToken(trimmed);
  if (pickupToken) {
    return {
      raw,
      value: pickupToken,
      kind: 'pickup',
      carrier: null,
      pickupToken,
    };
  }

  // 2. Etiqueta com payload JSON
  const json = decodeJsonLabel(trimmed);
  if (json) {
    return {
      raw,
      value: normalizeCode(json.code),
      kind: 'tracking',
      carrier: json.carrier,
      pickupToken: null,
    };
  }

  // 3. URL de transportadora: o codigo costuma estar na query ou no ultimo
  //    segmento do caminho.
  const url = asUrl(trimmed);
  if (url) {
    const domain = CARRIER_DOMAINS.find(([pattern]) => pattern.test(url.hostname));
    const fromQuery =
      url.searchParams.get('codigo') ??
      url.searchParams.get('tracking') ??
      url.searchParams.get('id');
    const candidate = fromQuery ?? url.pathname.split('/').filter(Boolean).pop() ?? trimmed;

    return {
      raw,
      value: normalizeCode(candidate),
      kind: 'tracking',
      carrier: domain?.[1] ?? null,
      pickupToken: null,
    };
  }

  const value = normalizeCode(trimmed);

  // 4. Chave de acesso da NF-e. Nao diz a transportadora, mas identifica o
  //    volume e e o que muita loja imprime como unico codigo de barras.
  if (/^\d{44}$/.test(value)) {
    return { raw, value, kind: 'invoice', carrier: null, pickupToken: null };
  }

  const rule = CARRIER_RULES.find((item) => item.test.test(value));
  return {
    raw,
    value,
    kind: rule ? 'tracking' : 'unknown',
    carrier: rule?.carrier ?? null,
    pickupToken: null,
  };
}

/** Rotulo curto para a UI dizer o que entendeu da leitura. */
export function describeScan(decoded: DecodedCode): string {
  if (decoded.kind === 'pickup') return 'QR de retirada';
  if (decoded.kind === 'invoice') return 'Chave da nota fiscal';
  if (decoded.carrier) return `Rastreio ${decoded.carrier}`;
  return 'Codigo lido';
}
